import React, { useState, useEffect } from 'react';
import { X, Bike, DollarSign } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AddressInput } from '../AddressInput';

interface EditCaseModalProps {
  caseId: string;
  onClose: () => void;
  onCaseUpdated: () => void;
}

export function EditCaseModal({ caseId, onClose, onCaseUpdated }: EditCaseModalProps) {
  const [caseData, setCaseData] = useState<any>(null);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [useDeliveryAsPostal, setUseDeliveryAsPostal] = useState(false);
  const [addressDetails, setAddressDetails] = useState<{
    postal?: { address: string; lat: number; lng: number };
    delivery?: { address: string; lat: number; lng: number };
  }>({});
  const [availableBikes, setAvailableBikes] = useState([]);
  const [selectedBikeId, setSelectedBikeId] = useState<string | null>(null);

  useEffect(() => {
    fetchCaseData();
    fetchAvailableBikes();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*, bike:bikes(*)')
        .eq('id', caseId)
        .single();

      if (error) throw error;
      setCaseData(data);
      
      // Initialize address details
      if (data.postal_address || data.delivery_address) {
        setAddressDetails({
          postal: data.postal_address ? { address: data.postal_address } : undefined,
          delivery: data.delivery_address ? { 
            address: data.delivery_address,
            ...(data.delivery_coordinates || {})
          } : undefined
        });
      }
    } catch (error) {
      console.error('Error fetching case:', error);
    }
  };

  const fetchAvailableBikes = async () => {
    try {
      const { data, error } = await supabase
        .from('bikes')
        .select('*')
        .eq('status', 'available')
        .order('make', { ascending: true });

      if (error) throw error;
      setAvailableBikes(data || []);
    } catch (error) {
      console.error('Error fetching available bikes:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const updateData = {
        not_at_fault_name: caseData.not_at_fault_name,
        claim_number: caseData.claim_number,
        not_at_fault_insurance_company: caseData.not_at_fault_insurance_company,
        not_at_fault_rego: caseData.not_at_fault_rego,
        at_fault_name: caseData.at_fault_name,
        at_fault_policy_number: caseData.at_fault_policy_number,
        at_fault_insurance_company: caseData.at_fault_insurance_company,
        at_fault_rego: caseData.at_fault_rego,
        postal_address: useDeliveryAsPostal && addressDetails.delivery ? 
          addressDetails.delivery.address : 
          addressDetails.postal?.address,
        delivery_address: addressDetails.delivery?.address,
        delivery_coordinates: addressDetails.delivery ? {
          lat: addressDetails.delivery.lat,
          lng: addressDetails.delivery.lng
        } : null,
        bike_id: selectedBikeId || caseData.bike_id
      };

      const { error } = await supabase
        .from('cases')
        .update(updateData)
        .eq('id', caseId);

      if (error) throw error;

      // If a new bike is being assigned, set its status to delivered
      if (selectedBikeId && selectedBikeId !== caseData.bike_id) {
        const selectedBike = availableBikes.find(bike => bike.id === selectedBikeId);
        const { error: bikeError } = await supabase
          .from('cases')
          .update({
            bike_id: selectedBikeId,
            daily_rate: selectedBike.rate_a,
            rental_start_date: new Date().toISOString(),
            status: 'bike_delivered'
          })
          .eq('id', caseId);

        if (bikeError) throw bikeError;
      }

      onCaseUpdated();
    } catch (error) {
      console.error('Error updating case:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCaseData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressSelect = (type: 'postal' | 'delivery', details: { address: string; lat: number; lng: number }) => {
    setAddressDetails(prev => ({
      ...prev,
      [type]: details
    }));

    if (type === 'delivery' && useDeliveryAsPostal) {
      setAddressDetails(prev => ({
        ...prev,
        postal: details
      }));
    }
  };

  if (!caseData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Edit Case</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Not at Fault Party */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Not at Fault Party</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="not_at_fault_name"
                  value={caseData.not_at_fault_name || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Claim Number</label>
                <input
                  type="text"
                  name="claim_number"
                  value={caseData.claim_number || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Insurance Company</label>
                <input
                  type="text"
                  name="not_at_fault_insurance_company"
                  value={caseData.not_at_fault_insurance_company || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Registration</label>
                <input
                  type="text"
                  name="not_at_fault_rego"
                  value={caseData.not_at_fault_rego || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
            </div>

            {/* At Fault Party */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">At Fault Party</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="at_fault_name"
                  value={caseData.at_fault_name || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Policy Number</label>
                <input
                  type="text"
                  name="at_fault_policy_number"
                  value={caseData.at_fault_policy_number || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Insurance Company</label>
                <input
                  type="text"
                  name="at_fault_insurance_company"
                  value={caseData.at_fault_insurance_company || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Registration</label>
                <input
                  type="text"
                  name="at_fault_rego"
                  value={caseData.at_fault_rego || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address
                </label>
                <AddressInput
                  value={addressDetails.delivery?.address || ''}
                  onChange={(address) => handleAddressSelect('delivery', { address, lat: 0, lng: 0 })}
                  onSelect={(details) => handleAddressSelect('delivery', details)}
                  placeholder="Enter delivery address..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="useDeliveryAsPostal"
                  checked={useDeliveryAsPostal}
                  onChange={(e) => {
                    setUseDeliveryAsPostal(e.target.checked);
                    if (e.target.checked && addressDetails.delivery) {
                      handleAddressSelect('postal', addressDetails.delivery);
                    }
                  }}
                  className="h-4 w-4 text-[#6563ff] focus:ring-[#6563ff] border-gray-300 rounded"
                />
                <label htmlFor="useDeliveryAsPostal" className="ml-2 block text-sm text-gray-900">
                  Use delivery address as postal address
                </label>
              </div>

              {!useDeliveryAsPostal && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Address
                  </label>
                  <AddressInput
                    value={addressDetails.postal?.address || ''}
                    onChange={(address) => handleAddressSelect('postal', { address, lat: 0, lng: 0 })}
                    onSelect={(details) => handleAddressSelect('postal', details)}
                    placeholder="Enter postal address..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bike Assignment Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bike Assignment</h3>
            
            {caseData.bike ? (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Currently Assigned Bike</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Make & Model</p>
                    <p className="font-medium">{caseData.bike.make} {caseData.bike.model}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Registration</p>
                    <p className="font-medium">{caseData.bike.rego_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Daily Rate</p>
                    <p className="font-medium">${caseData.bike.rate_a}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {availableBikes.length > 0 ? (
                    availableBikes.map(bike => (
                      <div 
                        key={bike.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedBikeId === bike.id 
                            ? 'border-[#6563ff] bg-[#6563ff]/5' 
                            : 'border-gray-200 hover:border-[#6563ff] hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedBikeId(bike.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <Bike className={`h-6 w-6 ${selectedBikeId === bike.id ? 'text-[#6563ff]' : 'text-gray-400'}`} />
                            <div>
                              <p className="font-medium text-gray-900">{bike.make} {bike.model}</p>
                              <p className="text-sm text-gray-500">Rego: {bike.rego_number}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Rate A</p>
                              <p className="font-medium text-gray-900">${bike.rate_a}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Rate B</p>
                              <p className="font-medium text-gray-900">${bike.rate_b}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No bikes available for assignment
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isAutoPopulating || isSaving}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAutoPopulating || isSaving}
              className="px-4 py-2 bg-[#6563ff] text-white rounded-lg hover:bg-[#5451ff] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}